import { useSyncExternalStore, type CSSProperties } from "react";

/**
 * The bottom band the consent bar occupies while it waits for an answer.
 * ConsentBanner publishes it (live, from a ResizeObserver) as a CSS variable on
 * <html>, so a page can lay its content out above the bar instead of under it,
 * and clears it once the bar is gone. Pages that ignore it are simply covered,
 * as before.
 */

export const CONSENT_BAR_RESERVE_VAR = "--consent-bar-reserve";
export const CONSENT_BAR_RESERVE_EVENT = "smartmanager:consent-bar-reserve";
/** Breathing room kept between page content and the bar's top edge. */
export const CONSENT_BAR_RESERVE_GAP_PX = 8;

/** `bottom` for a layer whose content must stay clear of the bar. */
export const CONSENT_BAR_RESERVE_BOTTOM = `var(${CONSENT_BAR_RESERVE_VAR}, 0px)`;
export const consentBarReserveBottomStyle: CSSProperties = { bottom: CONSENT_BAR_RESERVE_BOTTOM };

let currentReservePx = 0;

/** Publishes the band the bar covers, measured from the viewport bottom; 0 clears it. */
export function publishConsentBarReserve(coveredPx: number): void {
  if (typeof window === "undefined") return;
  const next = coveredPx > 0 ? Math.ceil(coveredPx) + CONSENT_BAR_RESERVE_GAP_PX : 0;
  if (next === currentReservePx) return;
  currentReservePx = next;
  const root = document.documentElement.style;
  if (next > 0) root.setProperty(CONSENT_BAR_RESERVE_VAR, `${next}px`);
  else root.removeProperty(CONSENT_BAR_RESERVE_VAR);
  window.dispatchEvent(new Event(CONSENT_BAR_RESERVE_EVENT));
}

export function readConsentBarReserve(): number {
  return currentReservePx;
}

export function subscribeToConsentBarReserve(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CONSENT_BAR_RESERVE_EVENT, listener);
  return () => window.removeEventListener(CONSENT_BAR_RESERVE_EVENT, listener);
}

/** The reserve in px, for layout math that cannot read the CSS variable. */
export function useConsentBarReserve(): number {
  return useSyncExternalStore(subscribeToConsentBarReserve, readConsentBarReserve, () => 0);
}
